import { trimLower } from "../../shared/utils/helper-methods";
import { monthStringToDate, fromExcelSerialToUTCDate } from "../../shared/utils/date-utils";

/**
 * Checks if a value is a valid non-zero dollar value
 */
function isValidDollarValue(value: any): boolean {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  
  const numValue = Number(value);
  return !isNaN(numValue) && numValue !== 0;
}

/**
 * Checks if cell A2 contains 'rebates required: false'
 * If so, the check should be skipped in the future
 */
async function isRebateCheckDisabled(context: Excel.RequestContext, sheet: Excel.Worksheet): Promise<boolean> {
  const cellA2 = sheet.getRange("A2");
  cellA2.load(["values"]);
  await context.sync();
  
  const aValue = cellA2.values[0][0];
  return trimLower(aValue) === "rebates required: false";
}

/**
 * Finds the row index for a given month string in Column A
 * The month string is expected to be in format "yyyy-mm"
 */
async function findMonthRowIndex(
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  monthStr: string
): Promise<number> {
  const targetDate = monthStringToDate(monthStr);
  const colA = sheet.getRange("A:A");
  const usedRange = colA.getUsedRangeOrNullObject();
  usedRange.load(["values", "rowIndex"]);
  await context.sync();
  
  if (usedRange.isNullObject) {
    return -1;
  }
  
  const values = usedRange.values as any[][];
  for (let i = 0; i < values.length; i++) {
    const cellValue = values[i][0];
    
    // Check if it's a date string in yyyy-mm format
    if (typeof cellValue === "string") {
      try {
        const cellDate = monthStringToDate(cellValue);
        if (cellDate.getUTCFullYear() === targetDate.getUTCFullYear() &&
            cellDate.getUTCMonth() === targetDate.getUTCMonth()) {
          return usedRange.rowIndex + i;
        }
      } catch (e) {
        // Not a valid date string, continue
      }
    }
    // Check if it's a number (Excel serial date)
    else if (typeof cellValue === "number") {
      const cellDate = fromExcelSerialToUTCDate(cellValue);
      if (cellDate.getUTCFullYear() === targetDate.getUTCFullYear() &&
          cellDate.getUTCMonth() === targetDate.getUTCMonth()) {
        return usedRange.rowIndex + i;
      }
    }
  }
  
  return -1;
}

/**
 * Checks if there are valid non-zero dollar values in column B
 * for the specified number of months back from the current month
 */
async function hasValidDollarValuesForMonths(
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  currentMonthRowIndex: number,
  monthsBack: number
): Promise<boolean> {
  const colB = sheet.getRange("B:B");
  const usedRange = colB.getUsedRangeOrNullObject();
  usedRange.load(["values", "rowIndex", "rowCount"]);
  await context.sync();
  
  if (usedRange.isNullObject) {
    return false;
  }
  
  const values = usedRange.values as any[][];
  const startRowIndex = Math.max(usedRange.rowIndex, currentMonthRowIndex - monthsBack);
  const endRowIndex = currentMonthRowIndex;
  
  // Check each row in the range
  for (let i = 0; i < values.length; i++) {
    const actualRowIndex = usedRange.rowIndex + i;
    if (actualRowIndex >= startRowIndex && actualRowIndex <= endRowIndex) {
      const value = values[i][0];
      if (isValidDollarValue(value)) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Main function to check rebate status
 * Returns true if the check passed (no action needed), false if user should be prompted
 */
export async function checkRebateStatus(selectedMonth: string): Promise<{ shouldContinue: boolean; userConfirmed: boolean }> {
  return Excel.run(async (context) => {
    const sheetName = "RX Rebates _ HRA Claims";
    const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
    sheet.load(["name"]);
    await context.sync();
    
    // If sheet doesn't exist, skip the check
    if (sheet.isNullObject) {
      console.log(`Sheet '${sheetName}' not found, skipping rebate check.`);
      return { shouldContinue: true, userConfirmed: false };
    }
    
    // Check if rebate check is disabled via A2 cell
    const isDisabled = await isRebateCheckDisabled(context, sheet);
    if (isDisabled) {
      console.log("Rebate check disabled via A2 cell, skipping.");
      return { shouldContinue: true, userConfirmed: false };
    }
    
    // Find the row for the selected month
    const monthRowIndex = await findMonthRowIndex(context, sheet, selectedMonth);
    if (monthRowIndex < 0) {
      console.log(`Could not find month '${selectedMonth}' in Column A, skipping rebate check.`);
      return { shouldContinue: true, userConfirmed: false };
    }
    
    // Check if there are any valid non-zero dollar values in the last 10 months
    const hasRebateHistory = await hasValidDollarValuesForMonths(context, sheet, monthRowIndex, 20);
    
    if (!hasRebateHistory) {
      // No values in last 10 months, no need to check further
      console.log("No valid dollar values found in last 10 months, skipping rebate check.");
      return { shouldContinue: true, userConfirmed: false };
    }
    
    // If we found values in the window
    const hasRecentHistory = await hasValidDollarValuesForMonths(context, sheet, monthRowIndex, 6);
    
    if (hasRecentHistory) {
      // Values found recent history
      console.log("Valid dollar values found in last 6 months, no alert needed.");
      return { shouldContinue: true, userConfirmed: false };
    }
    
    // No recent history need to alert user
    console.log("No valid dollar values found in last 6 months, alerting user.");
    return { shouldContinue: false, userConfirmed: false };
  });
}

/**
 * Sets the rebate required flag to false in cell A2
 */
export async function setRebateNotRequired(): Promise<void> {
  return Excel.run(async (context) => {
    const sheetName = "RX Rebates _ HRA Claims";
    const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
    sheet.load(["name"]);
    await context.sync();
    
    if (sheet.isNullObject) {
      throw new Error(`Sheet '${sheetName}' not found.`);
    }
    
    const cellA2 = sheet.getRange("A2");
    cellA2.values = [["rebates required: false"]];
    await context.sync();
  });
}
